import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import SiteFooter from "@/components/SiteFooter";
import { LiveNumber } from "@/components/LiveNumber";
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  tealBg: "#E6F5F4",
  muted: "#6B85A3",
  white: "#FFFFFF",
  green: "#16A34A",
  border: "#D6E4F0",
};

// ─── Fiscal helpers (frontend only — zero Supabase) ───────────────────────────
const TAUX_CIPAV = 0.234;
const BAREME_IR = [
  { min: 0, max: 11600, taux: 0 },
  { min: 11600, max: 29579, taux: 0.11 },
  { min: 29579, max: 84577, taux: 0.30 },
  { min: 84577, max: 181917, taux: 0.41 },
  { min: 181917, max: Infinity, taux: 0.45 },
];

function calculIR(r: number) {
  let ir = 0;
  for (const t of BAREME_IR) {
    if (r <= t.min) break;
    ir += (Math.min(r, t.max) - t.min) * t.taux;
  }
  return Math.round(ir);
}

function formatEur(n: number) {
  return n.toLocaleString("fr-FR") + "\u00a0€";
}


// ─── SECTION 1 — HERO + SIMULATEUR 3 ÉTAPES ─────────────────────────────────
function HeroSection() {
  const navigate = useNavigate();
  const [tarif, setTarif] = useState(70);
  const [patients, setPatients] = useState(20);
  const [charges, setCharges] = useState(8000);
  const [semaines, setSemaines] = useState(45);
  const [isReel, setIsReel] = useState(false);

  // CA from tarif + patients + semaines
  const ca = tarif * patients * semaines;

  // Micro-BNC CIPAV : net = CA - cotisations (23,4%) - charges réelles
  const cotisationsMicro = Math.round(ca * TAUX_CIPAV);
  const netMicro = Math.max(0, ca - cotisationsMicro - charges);

  // BNC Classique : net = (CA - charges) - bénéfice * 40% (estimation URSSAF/CIPAV)
  const beneficeNet = Math.max(0, ca - charges);
  const cotisationsBNC = Math.round(beneficeNet * 0.40);
  const netBNC = Math.max(0, beneficeNet - cotisationsBNC);

  // Valeurs affichées selon le toggle
  const netActuel = isReel ? netBNC : netMicro;
  const netMensuel = Math.round(netActuel / 12);
  const tauxHoraire = patients > 0 ? Math.round(netActuel / (patients * semaines)) : 0;
  const regimeLabel = isReel
    ? "BNC Classique (Cotisations ~40% du bénéfice)"
    : "Micro-BNC (CIPAV 23,2% + CFP 0,2%)";

  // Gain potentiel (toujours BNC vs Micro)
  const economieAnnuelle = charges > 0 ? netBNC - netMicro : 0;
  const economieMensuelle = Math.round(economieAnnuelle / 12);
  const roiMois = economieAnnuelle > 0 ? Math.ceil((564 / economieAnnuelle) * 12) : null;

  // Nudge fiscal : charges/CA >= 34% → BNC Classique recommandé (seuil abattement Micro)
  const tauxCharges = ca > 0 ? charges / ca : 0;

  const handleCTA = () => {
    try {
      localStorage.setItem("psygestion_landing_sim", JSON.stringify({ tarif, patients, charges, semaines }));
    } catch {}
    navigate("/auth?mode=signup");
  };

  const ctaLabel =
    economieAnnuelle > 500
      ? `Récupérer mes ${formatEur(economieAnnuelle)}/an → Simuler mon Revenu Net`
      : "Simuler mon Revenu Net →";

  return (
    <section style={{ backgroundColor: C.white, padding: "48px 16px 48px" }} className="sm:!py-[72px] sm:!px-5">
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 48,
          alignItems: "center",
        }}
        className="hero-grid"
      >
        {/* LEFT — Accroche */}
        <div>
          <span
            style={{
              display: "inline-block",
              fontSize: 12,
              fontWeight: 700,
              padding: "6px 14px",
              borderRadius: 20,
              backgroundColor: C.tealBg,
              color: C.teal,
              marginBottom: 20,
            }}
          >
            🧠 Conçu exclusivement pour les psychologues libéraux
          </span>

          <h1
            style={{
              fontSize: "clamp(26px, 4vw, 46px)",
              fontWeight: 800,
              color: C.navy,
              lineHeight: 1.15,
              marginBottom: 16,
              letterSpacing: "-0.5px",
            }}
          >
            Vous gagnez bien.
            <br />
            <span style={{ color: C.teal }}>Mais combien vous reste-t-il vraiment ?</span>
          </h1>

          <p style={{ fontSize: 17, color: C.muted, lineHeight: 1.7, marginBottom: 20 }}>
            Pour les psychologues libérales en Micro-BNC qui approchent du plafond — et ne savent pas
            si passer au Réel va les aider ou les noyer.{" "}
            <strong style={{ color: C.navy }}>PsyGestion vous répond en chiffres, pas en jargon.</strong>
          </p>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {[
              "Sachez enfin ce qu'il vous reste après URSSAF, CIPAV et impôts",
              "Micro ou Réel : réponse chiffrée sur votre situation exacte",
              "Zéro tableur. Zéro dimanche soir perdu. Zéro surprise.",
            ].map((t) => (
              <li key={t} style={{ display: "flex", gap: 10, fontSize: 15, color: C.navy }}>
                <span style={{ color: C.teal, fontWeight: 700, flexShrink: 0 }}>✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — Simulateur card 3 étapes */}
        <div
          style={{
            backgroundColor: C.white,
            borderRadius: 16,
            boxShadow: "0 8px 40px rgba(27,58,92,.14)",
            padding: "24px 16px",
            maxWidth: 520,
          }}
          className="sm:!p-[32px_28px]"
        >
          {/* ── ÉTAPE 1 — Inputs ── */}
          <h2 style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginBottom: 20 }}>
            Combien vous reste-t-il vraiment ?
          </h2>

          {/* Tarif + Patients côte à côte */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: 700,
                  color: C.navy,
                  fontSize: 13,
                  marginBottom: 6,
                }}
              >
                Tarif par séance
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="number"
                  min={30}
                  max={200}
                  value={tarif}
                  onChange={(e) => setTarif(Math.max(0, Number(e.target.value)))}
                  style={{
                    width: "100%",
                    padding: "10px 32px 10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.navy,
                    boxSizing: "border-box",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 14,
                    color: C.muted,
                    pointerEvents: "none",
                  }}
                >
                  €
                </span>
              </div>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: 700,
                  color: C.navy,
                  fontSize: 13,
                  marginBottom: 6,
                }}
              >
                Patients/semaine
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={patients}
                onChange={(e) => setPatients(Math.max(0, Number(e.target.value)))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.navy,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Charges — slider + input */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "block",
                fontWeight: 700,
                color: C.navy,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              Charges annuelles estimées
            </label>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input
                type="range"
                min={0}
                max={50000}
                step={500}
                value={charges}
                onChange={(e) => setCharges(Number(e.target.value))}
                className="touch-pan-x"
                style={{
                  flex: 1,
                  accentColor: C.teal,
                  cursor: "pointer",
                  height: 44,
                  minHeight: 44,
                }}
              />
              <input
                type="text"
                value={charges.toLocaleString("fr-FR")}
                onChange={(e) => {
                  const v = parseInt(e.target.value.replace(/\D/g, ""), 10);
                  if (!isNaN(v)) setCharges(Math.min(50000, Math.max(0, v)));
                }}
                style={{
                  width: 90,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.navy,
                  textAlign: "right",
                }}
              />
              <span style={{ fontSize: 14, color: C.muted }}>€</span>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              Loyer, supervision, formations, téléphone, assurances...
            </p>
            {/* Nudge fiscal dynamique */}
            {ca > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 10,
                  padding: "5px 13px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  transition: "all 0.3s",
                  backgroundColor: tauxCharges >= 0.34 ? "#FFF7ED" : "#DCFCE7",
                  color: tauxCharges >= 0.34 ? "#EA580C" : "#16A34A",
                }}
              >
                {tauxCharges >= 0.34
                  ? "Bascule BNC Classique recommandée ⚠️"
                  : "Régime Micro-BNC Optimal ✅"}
              </div>
            )}
          </div>

          {/* Semaines travaillées */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 700, color: C.navy, fontSize: 13, marginBottom: 6 }}>
              Semaines travaillées par an
            </label>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input
                type="range"
                min={30}
                max={52}
                step={1}
                value={semaines}
                onChange={(e) => setSemaines(Number(e.target.value))}
                className="touch-pan-x"
                style={{ flex: 1, accentColor: C.teal, cursor: "pointer", height: 44, minHeight: 44 }}
              />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.navy, minWidth: 24, textAlign: "right" }}>{semaines}</span>
              <span style={{ fontSize: 14, color: C.muted }}>sem.</span>
            </div>
          </div>

          {/* ── Toggle régime ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderRadius: 10,
              backgroundColor: isReel ? C.tealBg : "#F7FAFE",
              margin: "20px 0 8px",
              transition: "background-color 0.2s",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: !isReel ? C.navy : C.muted }}>
              Micro-BNC
            </span>
            <Switch
              checked={isReel}
              onCheckedChange={setIsReel}
              className="data-[state=checked]:bg-[#2A9D8F]"
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: isReel ? C.teal : C.muted }}>
              BNC Classique
            </span>
          </div>

          {/* ── ÉTAPE 2 — Situation actuelle ── */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 16px" }}
          >
            <div style={{ flex: 1, height: 1, backgroundColor: C.teal + "40" }} />
            <span
              style={{ fontSize: 13, fontWeight: 700, color: C.teal, whiteSpace: "nowrap" }}
            >
              📊 Votre situation actuelle
            </span>
            <div style={{ flex: 1, height: 1, backgroundColor: C.teal + "40" }} />
          </div>

          <div style={{ display: "grid", gap: 8, marginBottom: 8 }} className="metrics-grid">
            <div
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 10,
                padding: "12px 14px",
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>CA annuel estimé</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>
                <LiveNumber value={ca} fmt={formatEur} />
              </p>
            </div>
            <div
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 10,
                padding: "12px 14px",
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Revenu net actuel</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>
                <LiveNumber value={netMensuel} fmt={formatEur} />/mois
              </p>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{regimeLabel}</p>
            </div>
            <div
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 10,
                padding: "12px 14px",
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Taux horaire réel</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>
                <LiveNumber value={tauxHoraire} fmt={formatEur} />/h
              </p>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Net après toutes charges
              </p>
            </div>
          </div>

          {/* ── ÉTAPE 3 — Gain PsyGestion ── */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0 16px" }}
          >
            <div style={{ flex: 1, height: 1, backgroundColor: C.teal + "40" }} />
            <span
              style={{ fontSize: 13, fontWeight: 700, color: C.teal, whiteSpace: "nowrap" }}
            >
              ✨ Avec PsyGestion
            </span>
            <div style={{ flex: 1, height: 1, backgroundColor: C.teal + "40" }} />
          </div>

          {economieAnnuelle > 0 ? (
            <div
              style={{
                backgroundColor: C.tealBg,
                border: `2px solid ${C.teal}`,
                borderRadius: 12,
                padding: "20px 16px",
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: C.teal,
                  lineHeight: 1.2,
                  marginBottom: 4,
                }}
              >
                + <LiveNumber value={economieAnnuelle} fmt={(n) => formatEur(n) + "/an"} baseColor={C.teal} /> dans votre poche
              </p>
              <p style={{ fontSize: 15, color: C.navy, marginBottom: 4 }}>
                En passant au BNC Classique avec vos charges actuelles
              </p>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
                Soit +<LiveNumber value={economieMensuelle} fmt={(n) => formatEur(n) + "/mois"} baseColor={C.muted} /> supplémentaires nets
              </p>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "5px 12px",
                  borderRadius: 20,
                  backgroundColor: "#DCFCE7",
                  color: C.green,
                  animation: "pulse 2s ease-in-out infinite",
                }}
              >
                💡 Économie identifiée — optimisation possible
              </span>
            </div>
          ) : (
            <div
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 12,
                padding: "20px 16px",
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              <p
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: C.navy,
                  lineHeight: 1.2,
                  marginBottom: 4,
                }}
              >
                Vous êtes sur le régime optimal
              </p>
              <p style={{ fontSize: 14, color: C.muted, marginBottom: 4 }}>
                Le Micro-BNC est avantageux pour votre situation actuelle
              </p>
              <p style={{ fontSize: 13, color: C.teal, marginBottom: 12 }}>
                PsyGestion vous alerte si cela change avec votre évolution
              </p>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "5px 12px",
                  borderRadius: 20,
                  backgroundColor: C.tealBg,
                  color: C.teal,
                }}
              >
                ✓ Régime optimisé
              </span>
            </div>
          )}

          {/* ROI block */}
          <div
            style={{
              backgroundColor: C.navy,
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 13, color: C.white, margin: 0 }}>
              PsyGestion à 47€/mois = 564€/an
            </p>
            {economieAnnuelle > 564 ? (
              <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, margin: "4px 0 0" }}>
                ROI : <LiveNumber value={Math.max(0, economieAnnuelle - 564)} fmt={formatEur} baseColor={C.teal} /> de gain net pour 564€
                investis{roiMois && roiMois <= 12 ? ` — rentable en ${roiMois} mois` : ""}
              </p>
            ) : (
              <p
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.8)",
                  margin: "4px 0 0",
                }}
              >
                Valeur principale : rappels URSSAF + assistant fiscal 24h/24 + suivi temps réel
              </p>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={handleCTA}
            style={{
              width: "100%",
              padding: "14px 28px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              border: "none",
              backgroundColor: C.teal,
              color: C.white,
              height: 48,
            }}
          >
            {ctaLabel}
          </button>
          <p
            style={{
              textAlign: "center",
              fontSize: 12,
              color: C.muted,
              marginTop: 8,
              fontStyle: "italic",
            }}
          >
            Rejoignez les membres fondateurs · Inscription en 2 minutes · Sans carte bancaire
          </p>
          <p
            style={{
              fontSize: 10,
              color: C.muted,
              fontStyle: "italic",
              marginTop: 8,
              textAlign: "center",
            }}
          >
            Estimation indicative — taux CIPAV 2026 officiels. Ne remplace pas un expert-comptable.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── SECTION 2 — PREUVES SOCIALES ────────────────────────────────────────────
const stats = [
  { value: "Zéro surprise", label: "Rappels URSSAF/CIPAV automatiques avant chaque échéance" },
  { value: "8 000 €/an", label: "Économie potentielle pour celles qui basculent au bon moment" },
  { value: "30 secondes", label: "Pour savoir si votre régime vous coûte de l'argent chaque mois" },
];

function SocialProofSection() {
  return (
    <section style={{ backgroundColor: "#F7FAFE", padding: "72px 20px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: C.navy, marginBottom: 12 }}>
          Ce que ça change, concrètement
        </h2>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 48 }}>
          Pas des fonctionnalités. Des résultats dans votre vie.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
          }}
        >
          {stats.map((s) => (
            <div
              key={s.value}
              style={{
                backgroundColor: C.white,
                borderRadius: 16,
                padding: "32px 24px",
                boxShadow: "0 2px 12px rgba(27,58,92,.06)",
              }}
            >
              <p style={{ fontSize: 36, fontWeight: 800, color: C.teal, marginBottom: 8 }}>
                {s.value}
              </p>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SECTION 2b — LE PONT MICRO → RÉEL ──────────────────────────────────────
function LePontSection() {
  const navigate = useNavigate();
  const steps = [
    {
      icon: "😰",
      phase: "Avant",
      title: "Le Micro-BNC, confortable mais flou",
      desc: "Simple à gérer. Mais au fond de vous, une question : est-ce que je laisse de l'argent sur la table chaque mois ?",
      bg: "#FFF7ED",
      border: "#FED7AA",
      color: "#EA580C",
    },
    {
      icon: "🤯",
      phase: "Le dilemme",
      title: "Passer au Réel : peur ou opportunité ?",
      desc: "Plus de paperasse ? Moins d'argent ? Ou enfin respirer parce que vos charges sont déduites pour de vrai ?",
      bg: "#F7FAFE",
      border: C.border,
      color: C.muted,
    },
    {
      icon: "✅",
      phase: "Avec PsyGestion",
      title: "Le point de bascule calculé pour vous",
      desc: "PsyGestion identifie exactement à partir de quand le Réel vous rapporte plus. Vous décidez avec des chiffres, pas des suppositions.",
      bg: C.tealBg,
      border: C.teal,
      color: C.teal,
    },
  ];

  return (
    <section style={{ backgroundColor: C.navy, padding: "72px 20px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: C.white,
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          Vous approchez du plafond Micro-BNC.
          <br />
          <span style={{ color: C.teal }}>Et maintenant ?</span>
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "rgba(255,255,255,0.65)",
            fontSize: 16,
            marginBottom: 48,
            maxWidth: 600,
            margin: "0 auto 48px",
          }}
        >
          C'est la décision qui peut vous faire récupérer des milliers d'euros — ou vous compliquer la vie inutilement. PsyGestion vous guide pas à pas.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            marginBottom: 40,
          }}
          className="features-grid"
        >
          {steps.map((s) => (
            <div
              key={s.phase}
              style={{
                backgroundColor: s.bg,
                borderRadius: 16,
                padding: "28px 24px",
                border: `2px solid ${s.border}`,
              }}
            >
              <span style={{ fontSize: 32, display: "block", marginBottom: 8 }}>{s.icon}</span>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 20,
                  backgroundColor: "rgba(0,0,0,0.06)",
                  color: s.color,
                  marginBottom: 10,
                }}
              >
                {s.phase}
              </span>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: C.navy, marginBottom: 8 }}>
                {s.title}
              </h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => navigate("/auth?mode=signup")}
            style={{
              padding: "14px 36px",
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 16,
              cursor: "pointer",
              border: `2px solid ${C.teal}`,
              backgroundColor: "transparent",
              color: C.teal,
            }}
          >
            Trouver mon point de bascule →
          </button>
          <p style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}>
            Gratuit · Sans inscription requise pour simuler
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── SECTION 3 — POURQUOI PSYGESTION (3 PILIERS) ─────────────────────────────
const pillars = [
  {
    icon: "🎯",
    label: "La Clarté",
    title: "Arrêtez de laisser de l'argent sur la table",
    desc: "Micro ou Réel : PsyGestion compare vos deux scénarios sur vos chiffres réels. Vous voyez exactement combien vous perdez — ou gagnez — selon votre régime.",
  },
  {
    icon: "🛡️",
    label: "La Tranquillité",
    title: "Zéro surprise URSSAF",
    desc: "Visualisez vos cotisations futures en temps réel. Sachez ce que vous pouvez dépenser ce mois-ci sans craindre l'appel de l'URSSAF en décembre.",
  },
  {
    icon: "⏳",
    label: "Le Temps",
    title: "Récupérez vos dimanches soirs",
    desc: "Fini le tableur Excel à 22h. Saisissez vos recettes en 30 secondes, PsyGestion calcule tout — cotisations, impôts, net réel — en temps réel.",
  },
];

function FeaturesSection() {
  return (
    <section style={{ backgroundColor: C.white, padding: "72px 20px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: C.navy,
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          Pourquoi PsyGestion ?
        </h2>
        <p style={{ textAlign: "center", color: C.muted, fontSize: 16, marginBottom: 48 }}>
          Conçu pour les deux questions qui reviennent toujours : "Suis-je sur le bon régime ?" et "Combien me reste-t-il vraiment ?"
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 24,
          }}
          className="features-grid"
        >
          {pillars.map((p) => (
            <div
              key={p.title}
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 16,
                padding: "32px 28px",
                border: `1px solid ${C.border}`,
                borderTop: `3px solid ${C.teal}`,
              }}
            >
              <span style={{ fontSize: 32, display: "block", marginBottom: 8 }}>{p.icon}</span>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 20,
                  backgroundColor: C.tealBg,
                  color: C.teal,
                  marginBottom: 12,
                }}
              >
                {p.label}
              </span>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 10 }}>
                {p.title}
              </h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Citation "Wit" */}
        <div
          style={{
            marginTop: 48,
            backgroundColor: C.navy,
            borderRadius: 16,
            padding: "28px 32px",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: 18, fontWeight: 700, color: C.white, lineHeight: 1.6, margin: 0 }}>
            "Le comptable vous dit ce que vous avez fait l'année dernière.
            <br />
            <span style={{ color: C.teal }}>PsyGestion vous dit ce que vous pouvez faire demain.</span>"
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── SECTION 4 — FAQ / OBJECTIONS ────────────────────────────────────────────
const faqItems = [
  {
    q: "Est-ce que ça remplace mon comptable ?",
    a: "Non — et c'est volontaire. PsyGestion vous aide à comprendre vos finances au quotidien. Votre comptable reste indispensable pour votre déclaration annuelle. Nos calculs sont des estimations préparatoires, pas une déclaration fiscale.",
  },
  {
    q: "Mes données financières sont-elles en sécurité ?",
    a: "Vos données sont hébergées en Europe (infrastructure AWS eu-west-1), chiffrées en transit et au repos. Nous ne vendons jamais vos données. Vous pouvez supprimer votre compte et toutes vos données en un clic depuis votre profil.",
  },
  {
    q: "47€/mois c'est rentable pour moi ?",
    a: "Si PsyGestion vous identifie au moins 500€ d'économies fiscales annuelles — ce qui arrive dans la majorité des cas — il se rembourse lui-même en moins d'un mois. Commencez gratuitement avec le plan Découverte pour le vérifier sur votre situation réelle.",
  },
  {
    q: "Je suis en Micro-BNC depuis toujours — c'est fait pour moi ?",
    a: "C'est exactement pour vous. 70% des psychologues libéraux sont en Micro-BNC sans avoir jamais vérifié si le BNC Classique serait plus avantageux. Notre simulateur vous répond en 2 minutes.",
  },
];

function FAQItem({ item }: { item: { q: string; a: string } }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.15)",
        paddingBottom: 16,
        marginBottom: 16,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: C.white, lineHeight: 1.5 }}>
          {item.q}
        </span>
        <ChevronDown
          size={20}
          color="rgba(255,255,255,0.6)"
          style={{
            flexShrink: 0,
            marginLeft: 16,
            transition: "transform .2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      {open && (
        <p
          style={{
            fontSize: 14,
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.7,
            marginTop: 12,
          }}
        >
          {item.a}
        </p>
      )}
    </div>
  );
}

function FAQSection() {
  return (
    <section style={{ backgroundColor: C.navy, padding: "72px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: C.white,
            textAlign: "center",
            marginBottom: 48,
          }}
        >
          Vos questions, nos réponses honnêtes
        </h2>
        {faqItems.map((item) => (
          <FAQItem key={item.q} item={item} />
        ))}
      </div>
    </section>
  );
}

// ─── SECTION 5 — CTA FINAL ──────────────────────────────────────────────────
function CTASection() {
  const navigate = useNavigate();
  return (
    <section style={{ backgroundColor: C.teal, padding: "72px 20px", textAlign: "center" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <h2
          style={{
            fontSize: 36,
            fontWeight: 800,
            color: C.white,
            lineHeight: 1.2,
            marginBottom: 16,
          }}
        >
          Finis les dimanches soirs sur Excel.
        </h2>
        <p style={{ fontSize: 18, color: "rgba(255,255,255,0.85)", marginBottom: 32, lineHeight: 1.6 }}>
          Rejoignez les membres fondateurs. Inscription en 2 minutes. Sans carte bancaire.
        </p>
        <button
          onClick={() => navigate("/auth?mode=signup")}
          style={{
            padding: "16px 40px",
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 17,
            cursor: "pointer",
            border: "none",
            backgroundColor: C.white,
            color: C.teal,
          }}
        >
          Simuler mon Net Réel →
        </button>
        <p
          style={{
            marginTop: 16,
            fontSize: 13,
            color: "rgba(255,255,255,0.7)",
            fontStyle: "italic",
          }}
        >
          Annulable à tout moment · Données supprimées sur demande
        </p>
      </div>
    </section>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
function SiteNav() {
  const navigate = useNavigate();
  return (
    <nav
      style={{
        position: "sticky", top: 0, zIndex: 100,
        backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 22 }}>🧠</span>
          <span style={{ fontWeight: 800, fontSize: 16, color: C.navy }}>PsyGestion</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => navigate("/auth?mode=login")}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              color: C.navy, background: "transparent", border: `1px solid ${C.border}`,
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.color = C.teal; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.navy; }}
          >
            Connexion
          </button>
          <button
            onClick={() => navigate("/auth?mode=signup")}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              color: "#fff", backgroundColor: C.teal, border: "none",
              cursor: "pointer",
            }}
          >
            Simuler mon Revenu Net →
          </button>
        </div>
      </div>
    </nav>
  );
}

// ─── PAGE ASSEMBLY ───────────────────────────────────────────────────────────
export default function LandingPage() {
  usePageTitle("Pilotage financier expert pour psychologues libéraux");
  return (
    <>
      <SiteNav />
      <HeroSection />
      <SocialProofSection />
      <LePontSection />
      <FeaturesSection />
      <FAQSection />
      <CTASection />
      <SiteFooter />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .metrics-grid {
            grid-template-columns: 1fr !important;
          }
          .features-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (min-width: 769px) {
          .metrics-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }
      `}</style>
    </>
  );
}
